import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Skeleton, Button, Space, Spin, Alert, notification } from 'antd';

import { handleAPIError } from '@services/base';
import {
  getPenalties,
  IPenalty,
  IPenaltyResult,
  updatePenalties,
} from '@services/penalty';
import useModal, { IModalPayload } from '@hooks/use-modal';

import DataGrid from '../PenaltyApplicationModal/components/DataGrid';
import { FileText } from 'lucide-react';

interface LockPayload {
  _id: string;
  status: string;
}

interface IProps {
  ids: string[];
  onClose: () => void;
}

const Widget = (props: IProps): JSX.Element | null => {
  const { ids, onClose } = props;
  const [, openResultModal] =
    useModal<IModalPayload<IPenaltyResult[]>>('penalty-result');
  const [data, setData] = useState<IPenalty[]>([]);
  const queryClient = useQueryClient();

  const { isFetching } = useQuery(
    ['update-penalty', ids.join('-')],
    () => {
      if (ids?.length) {
        return getPenalties({ ids: ids, page: 1, page_size: ids?.length });
      }
    },
    {
      keepPreviousData: false,
      onSuccess: res => {
        setData(
          (res?.penalties || []).map(item => {
            const { status } = item;
            return {
              ...item,
              message:
                ['APPLIED'].indexOf(status || '') < 0 ? 'Invalid status' : '',
            };
          }),
        );
      },
      onError: handleAPIError,
    },
  );
  const { isLoading: isSubmitting, mutate } = useMutation(
    (payloads: LockPayload[]) => {
      return updatePenalties(payloads);
    },
    {
      onSuccess: async res => {
        await queryClient.invalidateQueries('penalties');
        onClose();
        if (res.every(({ success }) => success)) {
          notification.success({
            message: 'Lock penalties',
            description: 'Locked penalties successfully',
          });
        } else {
          openResultModal({ isOpen: true, payload: res || [] });
        }
      },
      onError: handleAPIError,
    },
  );

  const submit = () => {
    const payload: LockPayload[] = data
      .filter(({ message }) => !message)
      .map(item => {
        return {
          _id: item._id,
          status: 'LOCKED',
        };
      });
    mutate(payload);
  };

  const onRemove = (id: string) => {
    const filtered = data.filter(({ _id }) => id !== _id);
    if (filtered.length) {
      setData(filtered);
    } else {
      onClose();
    }
  };

  if (isFetching) {
    return (
      <>
        <Skeleton />
        <Skeleton />
      </>
    );
  }

  if (data) {
    const validItems = data.filter(({ message }) => !message);
    const isValid = validItems.length;

    return (
      <Spin spinning={isSubmitting}>
        {validItems.length && validItems.length < data.length ? (
          <>
            <Alert
              message="The invalid items will be ignored after clicking ‘Confirm‘"
              type="warning"
              showIcon
            />
            <br />
          </>
        ) : null}
        {validItems.length === 0 ? (
          <>
            <Alert message="No valid items" type="error" showIcon />
            <br />
          </>
        ) : null}
        <DataGrid data={data} onRemove={onRemove} />
        <br />

        <div className={'text-center'}>
          <Space align={'center'}>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={!isValid}
              type="primary"
              icon={<FileText size={14} />}
            >
              Confirm
            </Button>
          </Space>
        </div>
      </Spin>
    );
  }

  return null;
};

export default Widget;
